import moment from "moment-timezone";
import React from "react";
import { Fonts, FontFamilies, FontWeights, FontColors } from "theme/fontConfig";

const TZ = "UTC";

/**
 * EventsList — reusable upcoming-events card list
 *
 * Props:
 *   events        {Array}    — same shape as react-big-calendar events
 *                              (must have `start` Date, `title`/`eventName`, and optionally `eventDetails`)
 *   onEventClick  {Function} — called with the event object when a card is clicked
 *   daysAhead     {number}   — how many days from today to include (default: 30)
 *   title         {string}   — panel header text (default: "Upcoming Events")
 *   showHeader    {boolean}  — show/hide the header row (default: true)
 *   maxHeight     {string}   — optional CSS max-height for a scrollable list (e.g. "480px")
 */
const EventsList = ({
  events,
  onEventClick,
  daysAhead = 30,
  title = "Upcoming Events",
  showHeader = true,
  maxHeight,
}) => {
  const today = moment().tz(TZ).startOf("day");
  const cutoff = moment().tz(TZ).startOf("day").add(daysAhead, "days").endOf("day");

  const upcoming = (events || [])
    .filter((e) => {
      const s = moment(e.start).tz(TZ);
      return s.isSameOrAfter(today) && s.isSameOrBefore(cutoff);
    })
    .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());

  return (
    <div style={{ fontFamily: FontFamilies.General }}>
      {showHeader && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: Fonts.GeneralSubHeaderFont.fontSize, fontWeight: Fonts.GeneralSubHeaderFont.fontWeight, color: FontColors.Slate0F172A }}>{title}</span>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: "#2555EF", color: FontColors.White, borderRadius: "50%",
            width: 20, height: 20, fontSize: Fonts.GeneralSmallLabelFont.fontSize, fontWeight: Fonts.GeneralSmallLabelFont.fontWeight, flexShrink: 0,
          }}>
            {upcoming.length}
          </span>
        </div>
      )}

      {upcoming.length === 0 ? (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", padding: "32px 16px", gap: 6,
        }}>
          <span style={{ fontSize: Fonts.GeneralPageHeaderFont.fontSize }}>📅</span>
          <span style={{ fontSize: Fonts.GeneralLabelFont.fontSize, fontWeight: Fonts.GeneralLabelFont.fontWeight, color: FontColors.Slate475569 }}>No upcoming events</span>
          <span style={{ fontSize: Fonts.GeneralCaptionFont.fontSize, color: FontColors.Slate94A3B8 }}>in the next {daysAhead} days</span>
        </div>
      ) : (
        <div style={{
          display: "flex", flexDirection: "column", gap: 10,
          ...(maxHeight ? { maxHeight, overflowY: "auto", paddingRight: 2 } : {}),
        }}>
          {upcoming.map((event, i) => (
            <EventCard key={event.id || i} event={event} onClick={onEventClick} />
          ))}
        </div>
      )}
    </div>
  );
};

const EventCard = ({ event, onClick }) => {
  const [hovered, setHovered] = React.useState(false);

  const displayDate = event._utcDate || event.start;
  const month = moment(displayDate).tz(TZ).format("MMM").toUpperCase();
  const day   = moment(displayDate).tz(TZ).format("DD");
  const dow   = moment(displayDate).tz(TZ).format("ddd").toUpperCase();
  const time  = moment(displayDate).tz(TZ).format("h:mm A");

  const eventType = event?.eventDetails?.eventType || "";
  const address   = event?.eventDetails?.eventAddress?.formattedAddress || "";
  const isOnline  = eventType.toLowerCase() === "online";

  return (
    <div
      onClick={() => onClick?.(event)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        border: `1px solid ${hovered ? "#2555EF" : "#E2E8F0"}`,
        borderRadius: 12,
        padding: 12,
        display: "flex",
        gap: 14,
        cursor: "pointer",
        transition: "border-color 0.15s, box-shadow 0.15s",
        boxShadow: hovered ? "0 2px 8px rgba(37,85,239,0.08)" : "none",
      }}
    >
      {/* Date badge */}
      <div style={{
        background: "#EEF2FF",
        borderLeft: "3px solid #2555EF",
        borderRadius: 10,
        padding: "10px 12px",
        textAlign: "center",
        minWidth: 56,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}>
        <span style={{ fontSize: Fonts.GeneralTinyFont.fontSize, fontWeight: FontWeights.SemiBold, color: FontColors.Blue2555EF, letterSpacing: "0.06em", lineHeight: 1 }}>{month}</span>
        <span style={{ fontSize: Fonts.GeneralHeaderFont.fontSize, fontWeight: Fonts.GeneralHeaderFont.fontWeight, color: FontColors.Slate0F172A, lineHeight: 1.1 }}>{day}</span>
        <span style={{ fontSize: Fonts.GeneralTinyFont.fontSize, fontWeight: FontWeights.SemiBold, color: FontColors.Slate64748B, letterSpacing: "0.06em", lineHeight: 1 }}>{dow}</span>
      </div>

      {/* Details */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4, justifyContent: "center" }}>
        <div style={{ fontSize: Fonts.GeneralSmallHeaderFont.fontSize, fontWeight: Fonts.GeneralSmallHeaderFont.fontWeight, color: FontColors.Slate0F172A, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {event.title || event.eventName}
        </div>

        {eventType && (
          <span style={{
            display: "inline-block", background: "#EEF2FF", color: FontColors.Blue2555EF,
            fontSize: Fonts.GeneralTinyFont.fontSize, fontWeight: FontWeights.SemiBold, textTransform: "uppercase",
            borderRadius: 20, padding: "2px 8px", letterSpacing: "0.04em",
            width: "fit-content",
          }}>
            {eventType}
          </span>
        )}

        <div style={{ fontSize: Fonts.GeneralCaptionFont.fontSize, color: FontColors.Slate64748B, display: "flex", alignItems: "center", gap: 4 }}>
          <span>🕑</span><span>{time}</span>
        </div>

        {address && (
          <div style={{ fontSize: Fonts.GeneralCaptionFont.fontSize, color: FontColors.Slate64748B, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {isOnline ? "🌐 Online" : `📍 ${address}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsList;
